import React, { useState, useEffect } from 'react';
import { 
  FaBook, 
  FaPen, 
  FaList, 
  FaBalanceScale, 
  FaChartLine, 
  FaLock,
  FaCog,
  FaPlus
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';

// Import des sous-composants
import PlanComptableView from '../components/accounting/PlanComptableView';
//import PlanComptableView from '../components/accounting/AccountingPage';
import SaisieEcriture from '../components/accounting/SaisieEcriture';
//import JournauxView from '../components/accounting/JournauxView'; A activer quand prêt
import NotFoundPageCompta from '../pages/NotFoundPageCompta';
//import GrandLivreView from '../components/accounting/GrandLivreView'; A activer quand prét
import BalanceView from '../components/accounting/BalanceView';
//import EtatsFinanciersView from '../components/accounting/EtatsFinanciersView'; A activer quand prét
//import ClotureExercice from '../components/accounting/ClotureExercice'; A activer quand prét


const ComptabilitePage = () => {
  const { hasPermission, currentUser } = useAuth();
  const [activeModule, setActiveModule] = useState('dashboard');
  const [exerciceActuel, setExerciceActuel] = useState(null);
  const [loading, setLoading] = useState(false);

  // Configuration des modules comptables
  const modules = [
    {
      id: 'dashboard',
      title: 'Tableau de bord',
      icon: FaChartLine,
      description: 'Vue d\'ensemble de la comptabilité',
      permission: null,
      color: 'bg-blue-500'
    },
    {
      id: 'plan-comptable',
      title: 'Plan Comptable',
      icon: FaBook,
      description: 'Gestion du plan de comptes OHADA',
      permission: null,
      color: 'bg-indigo-500'
    },
    {
      id: 'saisie',
      title: 'Saisie d\'écritures',
      icon: FaPen,
      description: 'Créer et modifier les écritures comptables',
      permission: 'canCreateEntries',
      color: 'bg-green-500'
    },
    {
      id: 'journaux',
      title: 'Journaux',
      icon: FaList,
      description: 'Consulter les journaux comptables',
      permission: null,
      color: 'bg-yellow-500'
    },
    {
      id: 'grand-livre',
      title: 'Grand Livre',
      icon: FaBook,
      description: 'Consulter le grand livre des comptes',
      permission: null,
      color: 'bg-purple-500'
    },
    {
      id: 'balance',
      title: 'Balance',
      icon: FaBalanceScale,
      description: 'Balance générale et auxiliaire',
      permission: null,
      color: 'bg-pink-500'
    },
    {
      id: 'etats-financiers',
      title: 'États Financiers',
      icon: FaChartLine,
      description: 'Bilan, compte de résultat, etc.',
      permission: null,
      color: 'bg-orange-500'
    },
    {
      id: 'cloture',
      title: 'Clôtures',
      icon: FaLock,
      description: 'Clôture mensuelle et annuelle',
      permission: 'canClosePeriods',
      color: 'bg-red-500'
    }
  ];

  // Filtrer les modules selon les permissions
  const modulesAutorises = modules.filter(module => 
    !module.permission || hasPermission(module.permission) || currentUser?.role === 'admin'
  );

  useEffect(() => {
    // Charger l'exercice comptable actuel au montage
    loadExerciceActuel();
  }, []);

  const loadExerciceActuel = async () => {
    try {
      setLoading(true);
      // Appel API pour récupérer l'exercice actuel
      // const response = await comptabiliteService.getExerciceActuel();
      // setExerciceActuel(response.data);
      
      // Simulation pour le moment
      setExerciceActuel({
        annee: 2025,
        date_debut: '2025-01-01',
        date_fin: '2025-12-31',
        statut: 'ouvert'
      });
    } catch (error) {
      console.error('Erreur chargement exercice:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderModuleGrid = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {modulesAutorises.map((module) => {
        const IconComponent = module.icon;
        return (
          <div
            key={module.id}
            onClick={() => setActiveModule(module.id)}
            className={`
              relative overflow-hidden rounded-xl shadow-lg cursor-pointer transform transition-all duration-200
              hover:scale-105 hover:shadow-xl
              ${activeModule === module.id ? 'ring-4 ring-primary-500' : ''}
            `}
          >
            {/* Gradient Background */}
            <div className={`${module.color} h-32 flex items-center justify-center relative`}>
              <div className="absolute inset-0 bg-black bg-opacity-20"></div>
              <IconComponent className="text-4xl text-white z-10" />
              
              {/* Badge pour les modules avec permissions spéciales */}
              {module.permission && (
                <div className="absolute top-2 right-2 bg-white bg-opacity-20 rounded-full p-1">
                  <FaCog className="text-white text-xs" />
                </div>
              )}
            </div>
            
            {/* Contenu */}
            <div className="p-4 bg-white">
              <h3 className="font-semibold text-gray-800 text-lg">{module.title}</h3>
              <p className="text-sm text-gray-600 mt-1">{module.description}</p>
            </div>
            
            {/* Indicateur actif */}
            {activeModule === module.id && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary-500"></div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Informations exercice */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Exercice Comptable</h2>
          <span className={`px-3 py-1 rounded-full text-sm ${
            exerciceActuel?.statut === 'ouvert' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {exerciceActuel?.statut?.toUpperCase()}
          </span>
        </div>
        
        {exerciceActuel && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span className="text-sm text-gray-500">Année</span>
              <p className="text-lg font-semibold">{exerciceActuel.annee}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Période</span>
              <p className="text-sm">{exerciceActuel.date_debut} au {exerciceActuel.date_fin}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Statut</span>
              <p className="text-sm font-medium">{exerciceActuel.statut}</p>
            </div>
          </div>
        )}
      </div>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <FaPen className="text-blue-600" />
            </div>
            <div className="ml-4">
              <span className="text-2xl font-bold text-gray-800">128</span>
              <p className="text-gray-600">Écritures du mois</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <FaList className="text-green-600" />
            </div>
            <div className="ml-4">
              <span className="text-2xl font-bold text-gray-800">5</span>
              <p className="text-gray-600">Journaux actifs</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100">
              <FaBalanceScale className="text-yellow-600" />
            </div>
            <div className="ml-4">
              <span className="text-2xl font-bold text-gray-800">42</span>
              <p className="text-gray-600">Comptes utilisés</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <FaLock className="text-purple-600" />
            </div>
            <div className="ml-4">
              <span className="text-2xl font-bold text-gray-800">11</span>
              <p className="text-gray-600">Périodes clôturées</p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions rapides */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Actions Rapides</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {hasPermission('canCreateEntries') && (
            <button
              onClick={() => setActiveModule('saisie')}
              className="flex items-center justify-center p-4 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors"
            >
              <FaPlus className="mr-2 text-green-600" />
              <span className="text-green-700 font-medium">Nouvelle écriture</span>
            </button>
          )}
          
          <button
            onClick={() => setActiveModule('balance')}
            className="flex items-center justify-center p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors"
          >
            <FaBalanceScale className="mr-2 text-blue-600" />
            <span className="text-blue-700 font-medium">Consulter la balance</span>
          </button>
          
          <button
            onClick={() => setActiveModule('journaux')}
            className="flex items-center justify-center p-4 bg-yellow-50 hover:bg-yellow-100 rounded-lg border border-yellow-200 transition-colors"
          >
            <FaList className="mr-2 text-yellow-600" />
            <span className="text-yellow-700 font-medium">Voir les journaux</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderActiveModule = () => {
    if (loading) return <LoadingSpinner />;

    switch (activeModule) {
      case 'dashboard':
        return renderDashboard();
      case 'plan-comptable':
        return <PlanComptableView />;
      case 'saisie':
        return <SaisieEcriture />;
      case 'journaux':
        //return <JournauxView />; A activer quand prêt
        return <NotFoundPageCompta/>;
      case 'grand-livre':
        //return <GrandLivreView />; A activer quand prêt
        return <NotFoundPageCompta/>;

      case 'balance':
        return <BalanceView />;
      case 'etats-financiers':
        //return <EtatsFinanciersView />; A activer quand prêt
        return <NotFoundPageCompta/>;

      case 'cloture':
        //return <ClotureExercice />; A activer quand prét
        return <NotFoundPageCompta/>;

      default:
        return renderModuleGrid();
    }
  };

  const getPageTitle = () => {
    const module = modules.find(m => m.id === activeModule);
    return module ? module.title : 'Comptabilité';
  };

  return (
    <div className="space-y-6">
      {/* En-tête avec navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {getPageTitle()}
              </h1>
              <p className="text-gray-600 mt-1">
                Module de gestion comptable OHADA
              </p>
            </div>
            
            {/* Informations exercice dans l'en-tête */}
            {exerciceActuel && (
              <div className="mt-4 md:mt-0 flex items-center space-x-4">
                <div className="text-right">
                  <div className="text-sm text-gray-500">Exercice</div>
                  <div className="font-semibold">{exerciceActuel.annee}</div>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                  exerciceActuel.statut === 'ouvert' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {exerciceActuel.statut}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation par onglets pour les modules */}
        {activeModule !== 'dashboard' && (
          <div className="px-6">
            <nav className="flex space-x-8 overflow-x-auto">
              <button
                onClick={() => setActiveModule('dashboard')}
                className="flex items-center space-x-2 py-4 px-1 border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap"
              >
                <FaChartLine />
                <span>Tableau de bord</span>
              </button>
              
              {modulesAutorises.filter(m => m.id !== 'dashboard').map((module) => {
                const IconComponent = module.icon;
                return (
                  <button
                    key={module.id}
                    onClick={() => setActiveModule(module.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeModule === module.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <IconComponent />
                    <span>{module.title}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        )}
      </div>

      {/* Contenu principal */}
      <div className="min-h-screen">
        {activeModule === 'dashboard' ? (
          <div className="space-y-8">
            {renderDashboard()}
            
            {/* Grille des modules en bas du dashboard */}
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Modules Comptables</h2>
              {renderModuleGrid()}
            </div>
          </div>
        ) : (
          renderActiveModule()
        )}
      </div>
    </div>
  );
};

export default ComptabilitePage;